import React from 'react';
import { SalesLayout } from '@/components/SalesLayout';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Pagination } from '@/components/Pagination';
import { useUploadGuard } from '@/components/UploadGuard';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Color palette for model cards
const MODEL_COLORS = [
  'from-blue-500 to-blue-600',
  'from-emerald-500 to-emerald-600',
  'from-purple-500 to-purple-600',
  'from-orange-500 to-orange-600',
  'from-cyan-500 to-cyan-600',
  'from-pink-500 to-pink-600',
  'from-indigo-500 to-indigo-600',
  'from-amber-500 to-amber-600',
];

const emptyStockItem = {
  brand: '',
  oem_model_name: '',
  variant: '',
  color: '',
  fuel_type: '',
  chassis_no: '',
  engine_no: '',
  controller_no: '',
  battery_no: '',
  charger_no: '',
  key_no: '',
  days: 0,
  location: '',
  bill_date: '',
  blocked_status: ''
};

function StockReport({ user, onLogout }) {
  const [stockData, setStockData] = React.useState([]);
  const [bookings, setBookings] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);
  const { ensureCompanyId, guardModal } = useUploadGuard(user);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [filterStatus, setFilterStatus] = React.useState('available');
  const [showAllotModal, setShowAllotModal] = React.useState(false);
  const [selectedStock, setSelectedStock] = React.useState(null);
  const [selectedBookingId, setSelectedBookingId] = React.useState('');
  const [bookingSearch, setBookingSearch] = React.useState('');
  const [sendingEmail, setSendingEmail] = React.useState(false);
  const [generatingLink, setGeneratingLink] = React.useState(false);
  
  // Add/Edit Modal state
  const [showStockModal, setShowStockModal] = React.useState(false);
  const [editingStock, setEditingStock] = React.useState(null);
  const [stockForm, setStockForm] = React.useState({ ...emptyStockItem });
  
  // Export/Share Modal state
  const [showExportModal, setShowExportModal] = React.useState(false);
  const [exportFormat, setExportFormat] = React.useState('excel');
  const [shareEmail, setShareEmail] = React.useState('');
  const [sharePhone, setSharePhone] = React.useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 10;

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [stockRes, bookingsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/stock`),
        axios.get(`${BACKEND_URL}/api/bookings?limit=10000`)
      ]);
      setStockData(stockRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const targetCo = await ensureCompanyId();
    if (!targetCo) { toast.info('Upload cancelled — no target company selected'); e.target.value = ''; return; }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('target_company_id', targetCo);

    try {
      setUploading(true);
      toast.info('Uploading stock file...');
      const response = await axios.post(`${BACKEND_URL}/api/stock/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Uploaded ${response.data.count} stock items`);
      fetchData();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Add new stock item
  const handleAddStock = () => {
    setEditingStock(null);
    setStockForm({ ...emptyStockItem });
    setShowStockModal(true);
  };

  // Edit stock item
  const handleEditStock = (item) => {
    setEditingStock(item);
    setStockForm({
      brand: item.brand || '',
      oem_model_name: item.oem_model_name || '',
      variant: item.variant || '',
      color: item.color || '',
      fuel_type: item.fuel_type || '',
      chassis_no: item.chassis_no || '',
      engine_no: item.engine_no || '',
      controller_no: item.controller_no || '',
      battery_no: item.battery_no || '',
      charger_no: item.charger_no || '',
      key_no: item.key_no || '',
      days: item.days || 0,
      location: item.location || '',
      bill_date: item.bill_date || '',
      blocked_status: item.blocked_status || ''
    });
    setShowStockModal(true);
  };

  // Save stock item (Add or Update)
  const handleSaveStock = async () => {
    if (!stockForm.chassis_no) {
      toast.error('Chassis No is required');
      return;
    }

    try {
      if (editingStock) {
        // Update existing
        await axios.put(`${BACKEND_URL}/api/stock/${editingStock.id}`, stockForm);
        toast.success('Stock item updated');
      } else {
        // Add new
        await axios.post(`${BACKEND_URL}/api/stock/add`, stockForm);
        toast.success('Stock item added');
      }
      setShowStockModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save stock item');
    }
  };

  // Delete stock item
  const handleDeleteStock = async (item) => {
    if (!window.confirm(`Are you sure you want to delete stock item with Chassis No: ${item.chassis_no}?`)) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/stock/${item.id}`);
      toast.success('Stock item deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete stock item');
    }
  };

  const handleAllotment = async () => {
    if (!selectedStock || !selectedBookingId) {
      toast.error('Please select a booking');
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/stock/allot`, {
        stock_id: selectedStock.id,
        booking_id: selectedBookingId
      });
      
      toast.success('Vehicle allotted successfully!');
      setShowAllotModal(false);
      setSelectedStock(null);
      setSelectedBookingId('');
      fetchData();
    } catch (error) {
      console.error('Allotment failed:', error);
      toast.error(error.response?.data?.detail || 'Allotment failed');
    }
  };

  const handleRemoveAllotment = async (stockItem) => {
    if (!window.confirm('Are you sure you want to remove this allotment?')) return;

    try {
      await axios.post(`${BACKEND_URL}/api/stock/remove-allotment`, {
        stock_id: stockItem.id
      });
      toast.success('Allotment removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove allotment');
    }
  };

  const handleSyncToGoogleSheets = async () => {
    try {
      setSyncing(true);
      toast.info('Syncing to Google Sheets...');
      await axios.post(`${BACKEND_URL}/api/stock/sync-to-sheets`);
      toast.success('Stock data synced to "Current Stock" sheet');
    } catch (error) {
      console.error('Sync failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to sync to Google Sheets');
    } finally {
      setSyncing(false);
    }
  };

  // Export to Excel
  const handleExportExcel = async () => {
    try {
      toast.info('Generating Excel...');
      const response = await axios.post(`${BACKEND_URL}/api/stock/export`, {
        stock_ids: filteredStock.map(s => s.id),
        format: 'excel'
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Stock_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel downloaded');
      return response.data;
    } catch (error) {
      toast.error('Failed to generate Excel');
      return null;
    }
  };

  // Export to PDF (backend generated)
  const handleExportPDF = async () => {
    try {
      toast.info('Generating PDF...');
      const response = await axios.post(`${BACKEND_URL}/api/stock/export`, {
        stock_ids: filteredStock.map(s => s.id),
        format: 'pdf'
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Stock_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
      return true;
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
      return false;
    }
  };

  // Print
  const handlePrint = async () => {
    try {
      toast.info('Preparing print...');
      const response = await axios.post(`${BACKEND_URL}/api/stock/export`, {
        stock_ids: filteredStock.map(s => s.id),
        format: 'pdf'
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      }
    } catch (error) {
      toast.error('Failed to prepare print');
    }
  };

  // Email share with attachment via backend
  const handleEmailShare = async () => {
    if (!shareEmail) {
      toast.error('Please enter email address');
      return;
    }
    
    try {
      setSendingEmail(true);
      toast.info('Sending email with attachment...');
      
      const response = await axios.post(`${BACKEND_URL}/api/stock/share-email`, {
        email: shareEmail,
        format: exportFormat,
        stock_ids: filteredStock.map(s => s.id)
      });
      
      if (response.data.status === 'success') {
        toast.success('Email sent successfully with attachment!');
        setShowExportModal(false);
        setShareEmail('');
      } else if (response.data.status === 'skipped') {
        toast.warning('Email service not configured. Please contact admin.');
      } else {
        toast.error(response.data.message || 'Failed to send email');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // WhatsApp share with download link
  const handleWhatsAppShare = async () => {
    try {
      setGeneratingLink(true);
      toast.info('Generating share link...');
      
      const response = await axios.post(`${BACKEND_URL}/api/stock/generate-share-link`, {
        format: exportFormat,
        stock_ids: filteredStock.map(s => s.id)
      });
      
      const shareLink = response.data.share_link;
      const summary = response.data.summary;
      
      const text = encodeURIComponent(
        `📊 *Stock Report*\n` +
        `📅 Date: ${new Date().toLocaleDateString()}\n\n` +
        `${summary}\n\n` +
        `📥 Download Report:\n${shareLink}\n\n` +
        `(Link expires in 24 hours)`
      );
      
      const url = sharePhone 
        ? `https://web.whatsapp.com/send?phone=${sharePhone}&text=${text}`
        : `https://web.whatsapp.com/send?text=${text}`;
      
      { const _a = document.createElement('a'); _a.href = url; _a.target = '_blank'; _a.rel = 'noopener noreferrer'; document.body.appendChild(_a); _a.click(); document.body.removeChild(_a); }
      toast.success('WhatsApp opened with download link!');
      setShowExportModal(false);
    } catch (error) {
      toast.error('Failed to generate share link');
    } finally {
      setGeneratingLink(false);
    }
  };

  // Calculate Model-wise and Color-wise metrics (only for available/not allotted)
  const availableStock = stockData.filter(item => !item.allotted_booking_id);
  
  const modelColorMetrics = React.useMemo(() => {
    const metrics = {};
    availableStock.forEach(item => {
      const model = item.oem_model_name || 'Unknown';
      const color = item.color || 'Unknown';
      
      if (!metrics[model]) {
        metrics[model] = { total: 0, colors: {} };
      }
      metrics[model].total += 1;
      
      if (!metrics[model].colors[color]) {
        metrics[model].colors[color] = 0;
      }
      metrics[model].colors[color] += 1;
    });
    
    return Object.entries(metrics)
      .sort((a, b) => b[1].total - a[1].total)
      .map(([model, data]) => ({
        model,
        total: data.total,
        colors: Object.entries(data.colors)
          .sort((a, b) => b[1] - a[1])
          .map(([color, count]) => ({ color, count }))
      }));
  }, [availableStock]);

  // Filter stock data
  const filteredStock = React.useMemo(() => {
    let result = stockData.filter(item => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || 
        item.oem_model_name?.toLowerCase().includes(searchLower) ||
        item.chassis_no?.toLowerCase().includes(searchLower) ||
        item.color?.toLowerCase().includes(searchLower) ||
        item.allotted_customer_name?.toLowerCase().includes(searchLower) ||
        item.allotted_booking_no?.toString().includes(searchLower);

      const matchesStatus = filterStatus === 'all' ||
        (filterStatus === 'available' && !item.allotted_booking_id) ||
        (filterStatus === 'allotted' && item.allotted_booking_id);

      return matchesSearch && matchesStatus;
    });

    // Sort by stock_age_days descending (oldest first)
    result.sort((a, b) => {
      const ageA = parseInt(a.stock_age_days) || 0;
      const ageB = parseInt(b.stock_age_days) || 0;
      return ageB - ageA;
    });

    return result;
  }, [stockData, searchTerm, filterStatus]);

  // Calculate the maximum stock age for highlighting
  const maxStockAge = React.useMemo(() => {
    if (filteredStock.length === 0) return 0;
    return Math.max(...filteredStock.map(item => item.stock_age_days || 0));
  }, [filteredStock]);

  // Determine which items are "oldest" (top 10% or at least the max)
  const oldestThreshold = React.useMemo(() => {
    if (filteredStock.length === 0) return 0;
    // Highlight items that are in the top 10% oldest or >= 90 days old
    const ages = filteredStock.map(item => item.stock_age_days || 0).sort((a, b) => b - a);
    const topIndex = Math.max(1, Math.floor(ages.length * 0.1));
    return Math.max(ages[topIndex - 1] || 0, 90);
  }, [filteredStock]);

  // Paginated data
  const paginatedStock = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredStock.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredStock, currentPage, itemsPerPage]);

  // Reset to page 1 when filter changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  const availableBookings = bookings.filter(b => {
    const searchLower = bookingSearch.toLowerCase();
    const matchesSearch = !bookingSearch ||
      b.booking_no?.toString().includes(bookingSearch) ||
      b.customer_first_name?.toLowerCase().includes(searchLower) ||
      b.customer_last_name?.toLowerCase().includes(searchLower) ||
      b.mobile_no_1?.includes(bookingSearch);
    
    const noChassisAssigned = !b.chassis_no;
    
    return matchesSearch && noChassisAssigned;
  });

  const openAllotModal = (stockItem) => {
    setSelectedStock(stockItem);
    setSelectedBookingId('');
    setBookingSearch('');
    setShowAllotModal(true);
  };

  const totalStock = stockData.length;
  const availableCount = stockData.filter(s => !s.allotted_booking_id).length;
  const allottedCount = stockData.filter(s => s.allotted_booking_id).length;
  const oldStockCount = stockData.filter(s => !s.allotted_booking_id && (s.stock_age_days || 0) >= 90).length;

  if (loading) {
    return (
      <SalesLayout user={user} onLogout={onLogout} title="Stock Report">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </SalesLayout>
    );
  }

  return (
    <SalesLayout user={user} onLogout={onLogout} title="Stock Report">
      {guardModal}
      <div className="space-y-6">
        {/* Header Actions */}
        <div className="bg-white p-4 rounded-xl border shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-bold text-gray-800">Current Vehicle Stock</h2>
              <a 
                href="https://www.mydmsconnect.com/CurrentVStck.aspx"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline"
              >
                Download from DMS →
              </a>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleAddStock}
                className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium text-sm"
              >
                + Add Stock
              </button>
              <label className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer font-medium text-sm">
                {uploading ? 'Uploading...' : 'Upload File'}
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
              <button
                onClick={() => setShowExportModal(true)}
                className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium text-sm"
              >
                Export / Share
              </button>
              <button
                onClick={handlePrint}
                className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium text-sm"
              >
                Print
              </button>
              <button
                onClick={handleSyncToGoogleSheets}
                disabled={syncing || stockData.length === 0}
                className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm disabled:bg-gray-400"
              >
                {syncing ? 'Syncing...' : 'Sync to Sheets'}
              </button>
            </div>
          </div>
        </div>

        {/* Model-wise Available Stock Metrics */}
        {modelColorMetrics.length > 0 && (
          <div className="bg-white p-4 rounded-xl border shadow-sm">
            <h3 className="text-md font-bold text-gray-800 mb-4">Available Stock by Model & Color</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 anim-grid-stagger">
              {modelColorMetrics.map((item, index) => (
                <div 
                  key={item.model}
                  className={`bg-gradient-to-br ${MODEL_COLORS[index % MODEL_COLORS.length]} rounded-xl p-4 text-white shadow-lg`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-bold text-lg">{item.model}</h4>
                    <span className="bg-white/20 px-2 py-1 rounded-full text-sm font-bold">
                      {item.total}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {item.colors.map(({ color, count }) => (
                      <div key={color} className="flex justify-between items-center text-sm">
                        <span className="opacity-90">{color}</span>
                        <span className="bg-white/20 px-2 rounded font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filter Tabs & Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 anim-grid-stagger">
          <div 
            onClick={() => setFilterStatus('all')}
            className={`p-4 rounded-xl cursor-pointer transition-all ${
              filterStatus === 'all' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white border hover:shadow-md'
            }`}
          >
            <p className={`text-sm ${filterStatus === 'all' ? 'text-white/80' : 'text-gray-500'}`}>Total Stock</p>
            <p className="text-3xl font-bold mt-1">{totalStock}</p>
          </div>
          <div 
            onClick={() => setFilterStatus('available')}
            className={`p-4 rounded-xl cursor-pointer transition-all ${
              filterStatus === 'available' ? 'bg-green-600 text-white shadow-lg' : 'bg-white border hover:shadow-md'
            }`}
          >
            <p className={`text-sm ${filterStatus === 'available' ? 'text-white/80' : 'text-gray-500'}`}>
              Not Allotted (Available)
            </p>
            <p className="text-3xl font-bold mt-1">{availableCount}</p>
          </div>
          <div 
            onClick={() => setFilterStatus('allotted')}
            className={`p-4 rounded-xl cursor-pointer transition-all ${
              filterStatus === 'allotted' ? 'bg-purple-600 text-white shadow-lg' : 'bg-white border hover:shadow-md'
            }`}
          >
            <p className={`text-sm ${filterStatus === 'allotted' ? 'text-white/80' : 'text-gray-500'}`}>Allotted</p>
            <p className="text-3xl font-bold mt-1">{allottedCount}</p>
          </div>
          <div 
            className="p-4 rounded-xl bg-gradient-to-br from-red-500 to-red-600 text-white shadow-lg"
          >
            <p className="text-sm text-white/80">Old Stock (90+ days)</p>
            <p className="text-3xl font-bold mt-1">{oldStockCount}</p>
            {oldStockCount > 0 && (
              <p className="text-xs text-white/70 mt-1">Needs attention</p>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="bg-white p-4 rounded-xl border shadow-sm">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <input
              type="text"
              placeholder="Search by Model, Chassis No, Color, Customer Name, Booking No..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 min-w-[300px] p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            {/* Top Right Pagination */}
            <Pagination
              currentPage={currentPage}
              totalItems={filteredStock.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              showInfo={true}
            />
          </div>
        </div>

        {/* Stock Table — premium gradient header + alternating rows */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-indigo-100/60 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ background: 'linear-gradient(135deg, #4338CA 0%, #4F46E5 50%, #6D28D9 100%)' }}>
                <tr>
                  <th className="text-left p-3 font-semibold text-white tracking-wide text-[12px] uppercase">S.No</th>
                  <th className="text-left p-3 font-semibold text-white tracking-wide text-[12px] uppercase">Model</th>
                  <th className="text-left p-3 font-semibold text-white tracking-wide text-[12px] uppercase">Color</th>
                  <th className="text-left p-3 font-semibold text-white tracking-wide text-[12px] uppercase">Chassis No</th>
                  <th className="text-left p-3 font-semibold text-white tracking-wide text-[12px] uppercase">Engine No</th>
                  <th className="text-left p-3 font-semibold text-white tracking-wide text-[12px] uppercase">Battery No</th>
                  <th className="text-left p-3 font-semibold text-white tracking-wide text-[12px] uppercase">Charger No</th>
                  <th className="text-left p-3 font-semibold text-white tracking-wide text-[12px] uppercase bg-white/10">Bill Date</th>
                  <th className="text-left p-3 font-semibold text-white tracking-wide text-[12px] uppercase bg-white/15">Stock Age ↓</th>
                  <th className="text-left p-3 font-semibold text-white tracking-wide text-[12px] uppercase bg-white/10">Allotted To</th>
                  <th className="text-left p-3 font-semibold text-white tracking-wide text-[12px] uppercase bg-white/10">Customer Name</th>
                  <th className="text-left p-3 font-semibold text-white tracking-wide text-[12px] uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedStock.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="p-8 text-center text-gray-500">
                      {stockData.length === 0 
                        ? 'No stock data. Upload a stock file or add manually.'
                        : 'No matching records found.'}
                    </td>
                  </tr>
                ) : (
                  paginatedStock.map((item, index) => {
                    const stockAge = item.stock_age_days || 0;
                    const isOldest = stockAge >= oldestThreshold && stockAge > 0;
                    const isVeryOld = stockAge >= 120;
                    const rowNumber = (currentPage - 1) * itemsPerPage + index + 1;
                    
                    return (
                      <tr 
                        key={item.id} 
                        className={`border-b border-indigo-50 transition-all duration-180 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-violet-50 ${
                          item.allotted_booking_id ? 'bg-purple-50/30' : (index % 2 === 0 ? 'bg-white' : 'bg-gradient-to-r from-indigo-50/40 to-violet-50/30')
                        } ${isOldest && !item.allotted_booking_id ? 'bg-red-50' : ''} ${
                          isVeryOld && !item.allotted_booking_id ? 'bg-red-100' : ''
                        }`}
                      >
                        <td className="p-3 text-gray-600">{rowNumber}</td>
                        <td className="p-3 font-medium">{item.oem_model_name}</td>
                        <td className="p-3">{item.color}</td>
                        <td className="p-3 font-mono text-xs">{item.chassis_no}</td>
                        <td className="p-3 font-mono text-xs">{item.engine_no}</td>
                        <td className="p-3 font-mono text-xs">{item.battery_no || '-'}</td>
                        <td className="p-3 font-mono text-xs">{item.charger_no || '-'}</td>
                        <td className="p-3 bg-blue-50/50 text-xs">
                          {item.bill_date ? (
                            <span>{item.bill_date}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className={`p-3 font-bold text-center ${
                          isVeryOld && !item.allotted_booking_id
                            ? 'bg-red-200 text-red-800' 
                            : isOldest && !item.allotted_booking_id
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-amber-50/50'
                        }`}>
                          <div className="flex flex-col items-center">
                            <span className="text-lg">{stockAge}</span>
                            <span className="text-[10px] font-normal text-gray-500">days</span>
                          </div>
                          {isOldest && !item.allotted_booking_id && stockAge === maxStockAge && (
                            <span className="text-[10px] bg-red-600 text-white px-1 rounded mt-1 inline-block">OLDEST</span>
                          )}
                        </td>
                        <td className="p-3 bg-purple-50/50">
                          {item.allotted_booking_no ? (
                            <span className="font-medium text-purple-700">{item.allotted_booking_no}</span>
                          ) : '-'}
                        </td>
                        <td className="p-3 bg-purple-50/50">
                          {item.allotted_customer_name || '-'}
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1 flex-wrap">
                            <button
                              onClick={() => handleEditStock(item)}
                              className="px-2 py-1 text-blue-600 hover:bg-blue-100 rounded text-xs"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteStock(item)}
                              className="px-2 py-1 text-red-600 hover:bg-red-100 rounded text-xs"
                            >
                              Delete
                            </button>
                            {item.allotted_booking_id ? (
                              <button
                                onClick={() => handleRemoveAllotment(item)}
                                className="px-2 py-1 text-orange-600 hover:bg-orange-100 rounded text-xs"
                              >
                                Unallot
                              </button>
                            ) : (
                              <button
                                onClick={() => openAllotModal(item)}
                                className="px-2 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700"
                              >
                                Allot
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {filteredStock.length > 0 && (
            <div className="p-3 bg-gray-50 text-sm text-gray-600 flex justify-between items-center">
              <span>Showing {filteredStock.length} of {stockData.length} items | Sorted by: Stock Age (Oldest First)</span>
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-amber-100 border border-amber-300 rounded"></span> Old Stock (90+ days)
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-red-200 border border-red-400 rounded"></span> Very Old (120+ days)
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Add/Edit Stock Modal */}
        {showStockModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-bold mb-4 text-gray-800">
                {editingStock ? 'Edit Stock Item' : 'Add New Stock Item'}
              </h3>
              
              <div className="space-y-4">
                {/* Vehicle Info Section */}
                <div className="bg-blue-50 p-3 rounded-lg">
                  <h4 className="text-sm font-semibold text-blue-800 mb-3">Vehicle Information</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Brand</label>
                      <input
                        type="text"
                        value={stockForm.brand}
                        onChange={(e) => setStockForm({ ...stockForm, brand: e.target.value })}
                        className="w-full p-2 border rounded-lg text-sm"
                        placeholder="e.g., BGauss"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Model *</label>
                      <input
                        type="text"
                        value={stockForm.oem_model_name}
                        onChange={(e) => setStockForm({ ...stockForm, oem_model_name: e.target.value })}
                        className="w-full p-2 border rounded-lg text-sm"
                        placeholder="e.g., C12i Max 2.0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Variant</label>
                      <input
                        type="text"
                        value={stockForm.variant}
                        onChange={(e) => setStockForm({ ...stockForm, variant: e.target.value })}
                        className="w-full p-2 border rounded-lg text-sm"
                        placeholder="e.g., Standard"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Color *</label>
                      <input
                        type="text"
                        value={stockForm.color}
                        onChange={(e) => setStockForm({ ...stockForm, color: e.target.value })}
                        className="w-full p-2 border rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Fuel Type</label>
                      <select
                        value={stockForm.fuel_type}
                        onChange={(e) => setStockForm({ ...stockForm, fuel_type: e.target.value })}
                        className="w-full p-2 border rounded-lg text-sm"
                      >
                        <option value="">Select</option>
                        <option value="Electric">Electric</option>
                        <option value="Petrol">Petrol</option>
                        <option value="Diesel">Diesel</option>
                        <option value="CNG">CNG</option>
                        <option value="Hybrid">Hybrid</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Days in Stock</label>
                      <input
                        type="number"
                        value={stockForm.days}
                        onChange={(e) => setStockForm({ ...stockForm, days: parseInt(e.target.value) || 0 })}
                        className="w-full p-2 border rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Technical Details Section */}
                <div className="bg-green-50 p-3 rounded-lg">
                  <h4 className="text-sm font-semibold text-green-800 mb-3">Technical Details</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Chassis No *</label>
                      <input
                        type="text"
                        value={stockForm.chassis_no}
                        onChange={(e) => setStockForm({ ...stockForm, chassis_no: e.target.value })}
                        className="w-full p-2 border rounded-lg font-mono text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Engine No</label>
                      <input
                        type="text"
                        value={stockForm.engine_no}
                        onChange={(e) => setStockForm({ ...stockForm, engine_no: e.target.value })}
                        className="w-full p-2 border rounded-lg font-mono text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Controller No</label>
                      <input
                        type="text"
                        value={stockForm.controller_no}
                        onChange={(e) => setStockForm({ ...stockForm, controller_no: e.target.value })}
                        className="w-full p-2 border rounded-lg font-mono text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Key No</label>
                      <input
                        type="text"
                        value={stockForm.key_no}
                        onChange={(e) => setStockForm({ ...stockForm, key_no: e.target.value })}
                        className="w-full p-2 border rounded-lg font-mono text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Battery No</label>
                      <input
                        type="text"
                        value={stockForm.battery_no}
                        onChange={(e) => setStockForm({ ...stockForm, battery_no: e.target.value })}
                        className="w-full p-2 border rounded-lg font-mono text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Charger No</label>
                      <input
                        type="text"
                        value={stockForm.charger_no}
                        onChange={(e) => setStockForm({ ...stockForm, charger_no: e.target.value })}
                        className="w-full p-2 border rounded-lg font-mono text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Other Details Section */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Other Details</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
                      <input
                        type="text"
                        value={stockForm.location}
                        onChange={(e) => setStockForm({ ...stockForm, location: e.target.value })}
                        className="w-full p-2 border rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Bill Date</label>
                      <input
                        type="date"
                        value={stockForm.bill_date}
                        onChange={(e) => setStockForm({ ...stockForm, bill_date: e.target.value })}
                        className="w-full p-2 border rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowStockModal(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-100 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveStock}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  {editingStock ? 'Update' : 'Add Stock'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Export/Share Modal */}
        {showExportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
              <h3 className="text-lg font-bold mb-4 text-gray-800">Export & Share Stock Report</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Export Format</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setExportFormat('excel')}
                      className={`flex-1 p-3 border rounded-lg ${exportFormat === 'excel' ? 'bg-green-100 border-green-500' : ''}`}
                    >
                      📊 Excel
                    </button>
                    <button
                      onClick={() => setExportFormat('pdf')}
                      className={`flex-1 p-3 border rounded-lg ${exportFormat === 'pdf' ? 'bg-red-100 border-red-500' : ''}`}
                    >
                      📄 PDF
                    </button>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Quick Actions</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={handleExportExcel}
                      className="p-3 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                    >
                      Download Excel
                    </button>
                    <button
                      onClick={handleExportPDF}
                      className="p-3 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                    >
                      Download PDF
                    </button>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Share via Email</p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      placeholder="Enter email address"
                      value={shareEmail}
                      onChange={(e) => setShareEmail(e.target.value)}
                      className="flex-1 p-2 border rounded-lg text-sm"
                    />
                    <button
                      onClick={handleEmailShare}
                      disabled={sendingEmail}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:bg-blue-400"
                    >
                      {sendingEmail ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Email will include report as attachment</p>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Share via WhatsApp</p>
                  <div className="flex gap-2">
                    <input
                      type="tel"
                      placeholder="Phone (optional)"
                      value={sharePhone}
                      onChange={(e) => setSharePhone(e.target.value)}
                      className="flex-1 p-2 border rounded-lg text-sm"
                    />
                    <button
                      onClick={handleWhatsAppShare}
                      disabled={generatingLink}
                      className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm disabled:bg-green-400"
                    >
                      {generatingLink ? 'Generating...' : 'WhatsApp'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Includes download link (expires in 24h)</p>
                </div>
              </div>

              <button
                onClick={() => setShowExportModal(false)}
                className="w-full mt-4 px-4 py-2 border rounded-lg hover:bg-gray-100 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Allotment Modal */}
        {showAllotModal && selectedStock && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-bold mb-4 text-gray-800">Allot Vehicle to Booking</h3>
              
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h4 className="font-semibold text-blue-800 mb-2">Vehicle Details</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p><span className="text-gray-600">Model:</span> <strong>{selectedStock.oem_model_name}</strong></p>
                  <p><span className="text-gray-600">Color:</span> <strong>{selectedStock.color}</strong></p>
                  <p><span className="text-gray-600">Chassis:</span> <strong className="font-mono">{selectedStock.chassis_no}</strong></p>
                  <p><span className="text-gray-600">Engine:</span> <strong className="font-mono">{selectedStock.engine_no}</strong></p>
                  <p><span className="text-gray-600">Days in Stock:</span> <strong>{selectedStock.days}</strong></p>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Booking (by Booking No, Customer Name, or Mobile)
                </label>
                <input
                  type="text"
                  placeholder="Search..."
                  value={bookingSearch}
                  onChange={(e) => setBookingSearch(e.target.value)}
                  className="w-full p-2 border rounded-lg"
                />
              </div>

              <div className="max-h-64 overflow-y-auto border rounded-lg">
                {availableBookings.length === 0 ? (
                  <p className="p-4 text-center text-gray-500">
                    {bookingSearch ? 'No matching bookings found' : 'No bookings without chassis assigned'}
                  </p>
                ) : (
                  availableBookings.slice(0, 20).map(booking => (
                    <div
                      key={booking.id}
                      onClick={() => setSelectedBookingId(booking.id)}
                      className={`p-3 border-b cursor-pointer hover:bg-gray-50 ${
                        selectedBookingId === booking.id ? 'bg-purple-100 border-purple-500' : ''
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-medium text-blue-600">{booking.booking_no}</span>
                          <span className="mx-2">-</span>
                          <span>{booking.customer_first_name} {booking.customer_last_name}</span>
                        </div>
                        <div className="text-sm text-gray-500">
                          {booking.mobile_no_1}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {booking.brand_name} {booking.model} {booking.variant} | {booking.color}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowAllotModal(false);
                    setSelectedStock(null);
                  }}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-100 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAllotment}
                  disabled={!selectedBookingId}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:bg-gray-400"
                >
                  Confirm Allotment
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SalesLayout>
  );
}

export default StockReport;
